import { Fragment, useState } from "react";
import { Empty, Pagination, Skeleton, Tag } from "antd";
import {
	AimOutlined,
	CalendarOutlined,
	CheckCircleOutlined,
	FileTextOutlined,
	FlagOutlined,
	MessageOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { describeRtpEventChanges, describeRtpEventSentence } from "@utils/rtp_utils";
import { useRtpEvents, useRtpMetricOptions } from "./rtpHooks";
import type { RtpEvent, RtpPlanDetail } from "./types";

interface EventLogTabProps {
	plan: RtpPlanDetail;
}

// Lifecycle turning points render as emphasized timeline cards; everything else stays a
// compact line, so scrolling the tab reads as a story instead of a uniform log.
const MILESTONE_EVENTS = new Set<RtpEvent["eventType"]>([
	"plan_created",
	"plan_started",
	"plan_completed",
	"plan_cancelled",
	"phase_completed",
	"criterion_signed_off",
]);

// Ordered so `blocks_shifted` (no trailing underscore) still resolves under the block group.
function eventVisual(eventType: RtpEvent["eventType"]) {
	const milestone = MILESTONE_EVENTS.has(eventType);
	if (eventType === "criterion_signed_off") return { icon: <CheckCircleOutlined />, variant: "signoff", milestone };
	if (eventType.startsWith("plan_")) return { icon: <FileTextOutlined />, variant: "plan", milestone };
	if (eventType.startsWith("phase_")) return { icon: <FlagOutlined />, variant: "phase", milestone };
	if (eventType.startsWith("criterion_")) return { icon: <AimOutlined />, variant: "crit", milestone };
	if (eventType.startsWith("block")) return { icon: <CalendarOutlined />, variant: "block", milestone };
	if (eventType.startsWith("note_")) return { icon: <MessageOutlined />, variant: "note", milestone };
	return { icon: <FileTextOutlined />, variant: "plan", milestone };
}

const eventDay = (event: RtpEvent) => dayjs(event.createdAt).format("MMM D, YYYY");

export const EventLogTab = ({ plan }: EventLogTabProps) => {
	const [page, setPage] = useState(1);
	const { events, total, pageSize, loading } = useRtpEvents(plan.id, page);
	const { byKey: metricByKey } = useRtpMetricOptions();

	// "Today · Day 6" anchors — calendar date plus the plan-relative day number.
	const dayLabel = (event: RtpEvent): string => {
		const d = dayjs(event.createdAt);
		const base = d.isSame(dayjs(), "day")
			? "Today"
			: d.isSame(dayjs().subtract(1, "day"), "day")
				? "Yesterday"
				: d.format("MMM D, YYYY");
		if (plan.startDate) {
			const dayNum = d.startOf("day").diff(dayjs(plan.startDate).startOf("day"), "day") + 1;
			if (dayNum >= 1) return `${base} · Day ${dayNum}`;
		}
		return base;
	};

	return (
		<div>
			{loading ? (
				<Skeleton active />
			) : events.length === 0 ? (
				<Empty description="No events yet" />
			) : (
				<div>
					{events.map((event, i) => {
						const { icon, variant } = eventVisual(event.eventType);
						const changes = describeRtpEventChanges(event, (key) => metricByKey[key]?.label);
						const showDay = i === 0 || eventDay(events[i - 1]) !== eventDay(event);
						return (
							<Fragment key={event.id}>
								{showDay ? <div className="rtp-ev-day">{eventDay(event)}</div> : null}
								<div className="rtp-ev">
									<div className="rtp-ev-rail">
										<div className={`rtp-ev-icon rtp-ev-icon--${variant}`}>{icon}</div>
										<div className="rtp-ev-line" />
									</div>
									<div className="rtp-ev-body">
										<span className="rtp-ev-sentence">
											<strong>{event.actorName}</strong> {describeRtpEventSentence(event)}
										</span>
										{event.payload?.signOffCleared === true ? (
											<Tag color="warning" style={{ marginLeft: 8 }}>
												sign-off cleared
											</Tag>
										) : null}
										<span className="rtp-ev-time">{dayjs(event.createdAt).format("h:mm A")}</span>
										{changes.length > 0 ? (
											<div className="rtp-ev-changes">
												{changes.map((change) => (
													<div key={change} className="rtp-ev-change">
														{change}
													</div>
												))}
											</div>
										) : null}
									</div>
								</div>
							</Fragment>
						);
					})}
				</div>
			)}

			<Pagination
				current={page}
				pageSize={pageSize}
				total={total}
				showSizeChanger={false}
				hideOnSinglePage
				onChange={setPage}
				style={{ marginTop: 16, textAlign: "right" }}
			/>
		</div>
	);
};
